import { useState, useEffect } from "react";

import { CHANGELOG_MANIFEST, type ChangelogEntry } from "./changelogManifest";
// JSON import natif via Vite — la version reste single source of truth dans
// tauri.conf.json, et le bundle inline juste la string.
import tauriConf from "../../../src-tauri/tauri.conf.json";

const APP_VERSION: string = (tauriConf as { version: string }).version;
const STORAGE_KEY = "dozka-last-seen-version-v1";
const IS_IN_TAURI =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

/**
 * Hook "What's new" — compare version courante (tauri.conf.json) avec la
 * derniere version vue par l'user (localStorage). Si differente ET qu'on
 * a une entree dans CHANGELOG_MANIFEST pour la version courante, expose
 * l'entry + une fonction `markRead()` qui store la version courante.
 *
 * Visible UNIQUEMENT dans Tauri. Les users web/PWA voient les changements
 * en live a chaque deploy, donc une modal n'a aucun sens.
 *
 * Cas first-launch (jamais ouvert) : on store discreetement la version
 * courante sans afficher la modal — on ne sait pas si l'user vient d'install
 * pour la 1ere fois ou d'update. Etre conservatif ici evite de polluer le
 * 1er onboarding.
 */
export interface UseChangelogResult {
  entry: ChangelogEntry | null;
  version: string;
  markRead: () => void;
}

export function useChangelog(): UseChangelogResult {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (!IS_IN_TAURI) return;
    if (typeof localStorage === "undefined") return;

    const lastSeen = localStorage.getItem(STORAGE_KEY);
    if (!lastSeen) {
      // First launch ever : on assume install fresh, on store sans show.
      localStorage.setItem(STORAGE_KEY, APP_VERSION);
      return;
    }
    if (lastSeen !== APP_VERSION && CHANGELOG_MANIFEST[APP_VERSION]) {
      setShouldShow(true);
    }
  }, []);

  const markRead = () => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, APP_VERSION);
    }
    setShouldShow(false);
  };

  return {
    entry: shouldShow ? CHANGELOG_MANIFEST[APP_VERSION] ?? null : null,
    version: APP_VERSION,
    markRead,
  };
}
